import { buildWorkspaceAgentGuidedVariantSkipOtherFilesClause } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePromptPhrases';
import {
  resolveWorkspaceAgentGuidedVariantPromptRenameSuffix,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';
import {
  type WorkspaceAgentGuidedVariantSaveDestination,
  withWorkspaceAgentGuidedVariantSaveDestinationAndRename,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';

export const WORKSPACE_AGENT_SMART_RESIZE_MAX_SIDE = 10000;
export const WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES = 5;
export const WORKSPACE_AGENT_SMART_RESIZE_MIN_SIDE = 16;
export type WorkspaceAgentSmartResizeExactSize = {
  by: 'exact-size';
  /**
   * Picker row id. Canvas social ids are saved as platform members.
   * Store, lifecycle, and shape ids are saved by their pixels, because canvas
   * only resolves platform members that are social format ids.
   */
  formatId?: string;
  height: number;
  label?: string;
  width: number;
};

export type WorkspaceAgentSmartResizeRatioSize = {
  by: 'ratio';
  height: number;
  width: number;
};

export type WorkspaceAgentSmartResizeSize = WorkspaceAgentSmartResizeExactSize | WorkspaceAgentSmartResizeRatioSize;

export type WorkspaceAgentSmartResizeChannelSize = {
  height: number;
  id: string;
  label: string;
  ratio: string;
  shortLabel: string;
  width: number;
};

export type WorkspaceAgentSmartResizeChannelGroup = {
  id: string;
  label: string;
  sizes: readonly WorkspaceAgentSmartResizeChannelSize[];
};

export type WorkspaceAgentSmartResizeSavedSize = {
  id: string;
  name: string;
  size: WorkspaceAgentSmartResizeSize;
};

export type WorkspaceAgentSmartResizeSizeSet = {
  id: string;
  name: string;
  sizes: readonly WorkspaceAgentSmartResizeSize[];
};

export type WorkspaceAgentSmartResizeShapeOption = {
  id: string;
  size: WorkspaceAgentSmartResizeSize;
};

const RATIO_NAMES: Record<string, string> = {
  '16:9': 'Wide',
  '1:1': 'Square',
  '4:5': 'Portrait',
  '9:16': 'Vertical',
};

type ChannelSizeInput = {
  height: number;
  id: string;
  label: string;
  ratio: string;
  shortLabel: string;
  width: number;
};

export const workspaceAgentSmartResizeChannelSize = ({
  height,
  id,
  label,
  ratio,
  shortLabel,
  width,
}: ChannelSizeInput): WorkspaceAgentSmartResizeChannelSize => ({
  height,
  id,
  label,
  ratio,
  shortLabel,
  width,
});

const channelSize = workspaceAgentSmartResizeChannelSize;

type PlatformSizeInput = {
  height: number;
  key: string;
  platformId: string;
  platformLabel: string;
  ratio: string;
  shortLabel: string;
  width: number;
};

const platformSize = ({
  height,
  key,
  platformId,
  platformLabel,
  ratio,
  shortLabel,
  width,
}: PlatformSizeInput): WorkspaceAgentSmartResizeChannelSize =>
  channelSize({
    height,
    id: `${platformId}_${key}`,
    label: `${platformLabel} ${shortLabel.charAt(0).toLowerCase()}${shortLabel.slice(1)}`,
    ratio,
    shortLabel,
    width,
  });

const META_OWN_SIZES = [
  platformSize({
    height: 1350,
    key: 'portrait',
    platformId: 'meta',
    platformLabel: 'Meta',
    ratio: '4:5',
    shortLabel: 'Portrait',
    width: 1080,
  }),
  platformSize({
    height: 1080,
    key: 'square',
    platformId: 'meta',
    platformLabel: 'Meta',
    ratio: '1:1',
    shortLabel: 'Square',
    width: 1080,
  }),
  platformSize({
    height: 1920,
    key: 'story_reel',
    platformId: 'meta',
    platformLabel: 'Meta',
    ratio: '9:16',
    shortLabel: 'Story and reel',
    width: 1080,
  }),
];

const INSTAGRAM_LANDSCAPE = platformSize({
  height: 566,
  key: 'feed_landscape',
  platformId: 'instagram',
  platformLabel: 'Instagram',
  ratio: '1.91:1',
  shortLabel: 'Landscape',
  width: 1080,
});
const FACEBOOK_LANDSCAPE = platformSize({
  height: 628,
  key: 'feed_landscape',
  platformId: 'facebook',
  platformLabel: 'Facebook',
  ratio: '1.91:1',
  shortLabel: 'Landscape',
  width: 1200,
});
const FACEBOOK_IN_STREAM = platformSize({
  height: 1080,
  key: 'in_stream_video',
  platformId: 'facebook',
  platformLabel: 'Facebook',
  ratio: '16:9',
  shortLabel: 'In-stream video',
  width: 1920,
});

/** Meta's section also lists the Instagram and Facebook sizes that share a buy. */
const asNestedPlatformRow = (size: WorkspaceAgentSmartResizeChannelSize): WorkspaceAgentSmartResizeChannelSize => ({
  ...size,
  shortLabel: size.label,
});

export const WORKSPACE_AGENT_SMART_RESIZE_ECOMMERCE_GROUP: WorkspaceAgentSmartResizeChannelGroup = {
  id: 'web_stores',
  label: 'Ecommerce',
  sizes: [
    channelSize({
      height: 2000,
      id: 'store_amazon',
      label: 'Amazon main image',
      ratio: '1:1',
      shortLabel: 'Amazon',
      width: 2000,
    }),
    channelSize({
      height: 2048,
      id: 'store_shopify',
      label: 'Shopify product',
      ratio: '1:1',
      shortLabel: 'Shopify',
      width: 2048,
    }),
  ],
};

export const WORKSPACE_AGENT_SMART_RESIZE_SOCIAL_GROUPS: readonly WorkspaceAgentSmartResizeChannelGroup[] = [
  {
    id: 'meta',
    label: 'Meta',
    sizes: [
      ...META_OWN_SIZES,
      asNestedPlatformRow(INSTAGRAM_LANDSCAPE),
      asNestedPlatformRow(FACEBOOK_LANDSCAPE),
      asNestedPlatformRow(FACEBOOK_IN_STREAM),
    ],
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    sizes: [
      platformSize({
        height: 1920,
        key: 'standard',
        platformId: 'tiktok',
        platformLabel: 'TikTok',
        ratio: '9:16',
        shortLabel: 'Standard',
        width: 1080,
      }),
      platformSize({
        height: 1080,
        key: 'square',
        platformId: 'tiktok',
        platformLabel: 'TikTok',
        ratio: '1:1',
        shortLabel: 'Square',
        width: 1080,
      }),
      platformSize({
        height: 1080,
        key: 'landscape',
        platformId: 'tiktok',
        platformLabel: 'TikTok',
        ratio: '16:9',
        shortLabel: 'Landscape',
        width: 1920,
      }),
    ],
  },
  {
    id: 'snapchat',
    label: 'Snap',
    sizes: [
      platformSize({
        height: 1920,
        key: 'story',
        platformId: 'snapchat',
        platformLabel: 'Snap',
        ratio: '9:16',
        shortLabel: 'Story',
        width: 1080,
      }),
      platformSize({
        height: 1920,
        key: 'spotlight',
        platformId: 'snapchat',
        platformLabel: 'Snap',
        ratio: '9:16',
        shortLabel: 'Spotlight',
        width: 1080,
      }),
    ],
  },
  {
    id: 'youtube',
    label: 'YouTube',
    sizes: [
      platformSize({
        height: 1080,
        key: 'standard_video',
        platformId: 'youtube',
        platformLabel: 'YouTube',
        ratio: '16:9',
        shortLabel: 'Standard video',
        width: 1920,
      }),
      platformSize({
        height: 1920,
        key: 'shorts',
        platformId: 'youtube',
        platformLabel: 'YouTube',
        ratio: '9:16',
        shortLabel: 'Shorts',
        width: 1080,
      }),
      platformSize({
        height: 720,
        key: 'thumbnail',
        platformId: 'youtube',
        platformLabel: 'YouTube',
        ratio: '16:9',
        shortLabel: 'Thumbnail',
        width: 1280,
      }),
    ],
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    sizes: [
      platformSize({
        height: 1350,
        key: 'feed_portrait',
        platformId: 'linkedin',
        platformLabel: 'LinkedIn',
        ratio: '4:5',
        shortLabel: 'Feed',
        width: 1080,
      }),
      platformSize({
        height: 1200,
        key: 'feed_square',
        platformId: 'linkedin',
        platformLabel: 'LinkedIn',
        ratio: '1:1',
        shortLabel: 'Feed',
        width: 1200,
      }),
      platformSize({
        height: 627,
        key: 'feed_landscape',
        platformId: 'linkedin',
        platformLabel: 'LinkedIn',
        ratio: '1.91:1',
        shortLabel: 'Feed',
        width: 1200,
      }),
      platformSize({
        height: 1920,
        key: 'video_vertical',
        platformId: 'linkedin',
        platformLabel: 'LinkedIn',
        ratio: '9:16',
        shortLabel: 'Video vertical',
        width: 1080,
      }),
      platformSize({
        height: 396,
        key: 'cover_banner',
        platformId: 'linkedin',
        platformLabel: 'LinkedIn',
        ratio: '4:1',
        shortLabel: 'Cover / banner',
        width: 1584,
      }),
    ],
  },
  {
    id: 'x',
    label: 'X',
    sizes: [
      platformSize({
        height: 900,
        key: 'single_image',
        platformId: 'x',
        platformLabel: 'X',
        ratio: '16:9',
        shortLabel: 'Single image',
        width: 1600,
      }),
      platformSize({
        height: 1200,
        key: 'in_stream_square',
        platformId: 'x',
        platformLabel: 'X',
        ratio: '1:1',
        shortLabel: 'In-stream',
        width: 1200,
      }),
      platformSize({
        height: 628,
        key: 'in_stream_landscape',
        platformId: 'x',
        platformLabel: 'X',
        ratio: '1.91:1',
        shortLabel: 'In-stream',
        width: 1200,
      }),
      platformSize({
        height: 500,
        key: 'header_banner',
        platformId: 'x',
        platformLabel: 'X',
        ratio: '3:1',
        shortLabel: 'Header / banner',
        width: 1500,
      }),
    ],
  },
];

export const WORKSPACE_AGENT_SMART_RESIZE_LIFECYCLE_GROUP: WorkspaceAgentSmartResizeChannelGroup = {
  id: 'lifecycle',
  label: 'Lifecycle',
  sizes: [
    channelSize({
      height: 400,
      id: 'lifecycle_email_header',
      label: 'Email header',
      ratio: '3:1',
      shortLabel: 'Email header',
      width: 1200,
    }),
    channelSize({
      height: 800,
      id: 'lifecycle_email_body',
      label: 'Email body image',
      ratio: '3:2',
      shortLabel: 'Email body image',
      width: 1200,
    }),
    channelSize({
      height: 220,
      id: 'lifecycle_substack_header',
      label: 'Substack email header',
      ratio: '5:1',
      shortLabel: 'Substack email header',
      width: 1100,
    }),
    channelSize({
      height: 1048,
      id: 'lifecycle_substack_post',
      label: 'Substack post image',
      ratio: '1456 × 1048',
      shortLabel: 'Substack post image',
      width: 1456,
    }),
    channelSize({
      height: 2000,
      id: 'lifecycle_in_app_interstitial',
      label: 'In-app interstitial',
      ratio: '3:5',
      shortLabel: 'In-app interstitial',
      width: 1200,
    }),
    channelSize({
      height: 512,
      id: 'lifecycle_push_notification',
      label: 'Push notification',
      ratio: '2:1',
      shortLabel: 'Push notification',
      width: 1024,
    }),
  ],
};

const ratioSize = ({ height, width }: { height: number; width: number }): WorkspaceAgentSmartResizeRatioSize => ({
  by: 'ratio',
  height,
  width,
});

export const WORKSPACE_AGENT_SMART_RESIZE_SHAPES: readonly WorkspaceAgentSmartResizeShapeOption[] = [
  { id: 'shape-1x1', size: ratioSize({ height: 1, width: 1 }) },
  { id: 'shape-4x5', size: ratioSize({ height: 5, width: 4 }) },
  { id: 'shape-9x16', size: ratioSize({ height: 16, width: 9 }) },
  { id: 'shape-16x9', size: ratioSize({ height: 9, width: 16 }) },
  {
    id: 'shape-board-cover',
    size: {
      by: 'exact-size',
      formatId: 'shape-board-cover',
      height: 560,
      label: 'Board cover',
      width: 3080,
    },
  },
];

export const WORKSPACE_AGENT_SMART_RESIZE_CHANNELS: readonly WorkspaceAgentSmartResizeChannelSize[] = [
  ...WORKSPACE_AGENT_SMART_RESIZE_ECOMMERCE_GROUP.sizes,
  ...WORKSPACE_AGENT_SMART_RESIZE_SOCIAL_GROUPS.flatMap((group) => group.sizes),
  ...WORKSPACE_AGENT_SMART_RESIZE_LIFECYCLE_GROUP.sizes,
];

const greatestCommonDivisor = (left: number, right: number): number =>
  right === 0 ? left : greatestCommonDivisor(right, left % right);

const clampSide = (side: number): number =>
  Math.min(WORKSPACE_AGENT_SMART_RESIZE_MAX_SIDE, Math.max(WORKSPACE_AGENT_SMART_RESIZE_MIN_SIDE, side));

export const workspaceAgentSmartResizeSizeKey = (size: WorkspaceAgentSmartResizeSize): string => {
  if (size.by === 'exact-size') {
    return `exact:${size.width}x${size.height}:${size.label ?? ''}`;
  }

  return `ratio:${size.width}x${size.height}`;
};

export const sameWorkspaceAgentSmartResizeSize = (
  left: WorkspaceAgentSmartResizeSize,
  right: WorkspaceAgentSmartResizeSize,
): boolean => {
  if (!sameWorkspaceAgentSmartResizeDimensions(left, right)) {
    return false;
  }

  if (left.by === 'exact-size' && right.by === 'exact-size') {
    return left.label === right.label;
  }

  return true;
};

/** Pixels or ratio only. A channel row and a saved preset are the same resize when these match. */
export const sameWorkspaceAgentSmartResizeDimensions = (
  left: WorkspaceAgentSmartResizeSize,
  right: WorkspaceAgentSmartResizeSize,
): boolean => left.by === right.by && left.width === right.width && left.height === right.height;

export const workspaceAgentSmartResizeSizeText = (size: WorkspaceAgentSmartResizeSize): string => {
  if (size.by === 'ratio') {
    return `${size.width}:${size.height}`;
  }

  return `${size.width} × ${size.height}`;
};

export const workspaceAgentSmartResizeSizeLabel = (size: WorkspaceAgentSmartResizeSize): string => {
  if (size.by === 'exact-size' && size.label) {
    return size.label;
  }

  return workspaceAgentSmartResizeSizeText(size);
};

export const workspaceAgentSmartResizeRatioLabel = (size: WorkspaceAgentSmartResizeSize): string | null => {
  if (size.by === 'ratio') {
    return RATIO_NAMES[workspaceAgentSmartResizeSizeText(size)] ?? null;
  }

  const divisor = greatestCommonDivisor(size.width, size.height);

  return `${size.width / divisor}:${size.height / divisor}`;
};

export const workspaceAgentSmartResizeChannelToSize = (
  channel: WorkspaceAgentSmartResizeChannelSize,
): WorkspaceAgentSmartResizeExactSize => ({
  by: 'exact-size',
  formatId: channel.id,
  height: channel.height,
  label: channel.label,
  width: channel.width,
});

export type WorkspaceAgentSmartResizeOptionRowChannel = {
  channel: WorkspaceAgentSmartResizeChannelSize;
  meta?: string | null;
};

export const WORKSPACE_AGENT_SMART_RESIZE_EXACT_SIZE_OPTION_ROW_CHANNEL = workspaceAgentSmartResizeChannelSize({
  height: 0,
  id: 'exact-size-picker',
  label: 'Exact size',
  ratio: 'W × H',
  shortLabel: 'Exact size',
  width: 0,
});

const workspaceAgentSmartResizeShapeOptionRowRatio = (size: WorkspaceAgentSmartResizeSize, label: string): string => {
  const badgeCandidate =
    size.by === 'ratio' ? workspaceAgentSmartResizeSizeText(size) : workspaceAgentSmartResizeRatioLabel(size);

  if (badgeCandidate && badgeCandidate !== label) {
    return badgeCandidate;
  }

  return `${size.width} × ${size.height}`;
};

export const workspaceAgentSmartResizeShapeOptionToOptionRowChannel = (
  shape: WorkspaceAgentSmartResizeShapeOption,
): WorkspaceAgentSmartResizeOptionRowChannel => {
  const { size } = shape;
  const ratioName = workspaceAgentSmartResizeRatioLabel(size);
  const shortLabel =
    size.by === 'exact-size' && size.label ? size.label : (ratioName ?? workspaceAgentSmartResizeSizeLabel(size));

  return {
    channel: workspaceAgentSmartResizeChannelSize({
      height: size.height,
      id: shape.id,
      label: shortLabel,
      ratio: workspaceAgentSmartResizeShapeOptionRowRatio(size, shortLabel),
      shortLabel,
      width: size.width,
    }),
    meta: size.by === 'exact-size' ? workspaceAgentSmartResizeSizeText(size) : null,
  };
};

export const workspaceAgentSmartResizeSavedSizeToOptionRowChannel = (
  saved: WorkspaceAgentSmartResizeSavedSize,
): WorkspaceAgentSmartResizeOptionRowChannel => {
  const { size } = saved;
  const shortLabel = saved.name;

  const pixels = `${size.width} × ${size.height}`;

  return {
    channel: workspaceAgentSmartResizeChannelSize({
      height: size.height,
      id: saved.id,
      label: shortLabel,
      ratio: pixels,
      shortLabel,
      width: size.width,
    }),
    meta: workspaceAgentSmartResizeSizeText(size),
  };
};

export const workspaceAgentSmartResizeSizeSetToOptionRowChannel = (
  sizeSet: WorkspaceAgentSmartResizeSizeSet,
): WorkspaceAgentSmartResizeOptionRowChannel => ({
  channel: workspaceAgentSmartResizeChannelSize({
    height: 0,
    id: sizeSet.id,
    label: sizeSet.name,
    ratio: '',
    shortLabel: sizeSet.name,
    width: 0,
  }),
  meta: `${sizeSet.sizes.length} sizes`,
});

export const isWorkspaceAgentSmartResizeSizeSelected = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
  size: WorkspaceAgentSmartResizeSize,
): boolean => sizes.some((selected) => sameWorkspaceAgentSmartResizeSize(selected, size));

export const toggleWorkspaceAgentSmartResizeSize = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
  size: WorkspaceAgentSmartResizeSize,
): { added: boolean; sizes: WorkspaceAgentSmartResizeSize[] } => {
  if (isWorkspaceAgentSmartResizeSizeSelected(sizes, size)) {
    return {
      added: false,
      sizes: sizes.filter((selected) => !sameWorkspaceAgentSmartResizeSize(selected, size)),
    };
  }

  if (sizes.length >= WORKSPACE_AGENT_SMART_RESIZE_MAX_SIZES) {
    return { added: false, sizes: [...sizes] };
  }

  return { added: true, sizes: [...sizes, size] };
};

export const sizeSetIsFullySelected = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
  sizeSet: WorkspaceAgentSmartResizeSizeSet,
): boolean =>
  sizeSet.sizes.every((member) => sizes.some((selected) => sameWorkspaceAgentSmartResizeDimensions(selected, member)));

/** A set adds every member that is not already picked, or removes them when they all are. */
export const toggleWorkspaceAgentSmartResizeSet = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
  sizeSet: WorkspaceAgentSmartResizeSizeSet,
): WorkspaceAgentSmartResizeSize[] => {
  if (sizeSetIsFullySelected(sizes, sizeSet)) {
    return sizes.filter(
      (selected) => !sizeSet.sizes.some((member) => sameWorkspaceAgentSmartResizeDimensions(member, selected)),
    );
  }

  const missing = sizeSet.sizes.filter(
    (member) => !sizes.some((selected) => sameWorkspaceAgentSmartResizeDimensions(selected, member)),
  );

  let next = [...sizes];

  for (const member of missing) {
    const result = toggleWorkspaceAgentSmartResizeSize(next, member);

    if (!result.added) {
      break;
    }

    next = result.sizes;
  }

  return next;
};

export const parseWorkspaceAgentSmartResizeQuery = (query: string): WorkspaceAgentSmartResizeSize | null => {
  const normalized = query.trim().toLowerCase();
  const exactMatch = /^(\d+)\s*[x×*]\s*(\d+)\s*(?:px)?$/.exec(normalized);

  if (exactMatch?.[1] && exactMatch[2]) {
    return {
      by: 'exact-size',
      height: clampSide(Number(exactMatch[2])),
      width: clampSide(Number(exactMatch[1])),
    };
  }

  const ratioMatch = /^(\d+)\s*:\s*(\d+)(?:\s+(\d+)\s*(?:px)?)?$/.exec(normalized);

  if (!ratioMatch?.[1] || !ratioMatch[2]) {
    return null;
  }

  const ratioWidth = Number(ratioMatch[1]);
  const ratioHeight = Number(ratioMatch[2]);

  if (ratioWidth < 1 || ratioHeight < 1) {
    return null;
  }

  if (!ratioMatch[3]) {
    const divisor = greatestCommonDivisor(ratioWidth, ratioHeight);

    return { by: 'ratio', height: ratioHeight / divisor, width: ratioWidth / divisor };
  }

  const longSide = clampSide(Number(ratioMatch[3])) / Math.max(ratioWidth, ratioHeight);

  return {
    by: 'exact-size',
    height: Math.round(ratioHeight * longSide),
    width: Math.round(ratioWidth * longSide),
  };
};

const includesQuery = (query: string, ...values: Array<string | null | undefined>): boolean =>
  values.some((value) => value?.toLowerCase().includes(query));

export type WorkspaceAgentSmartResizeSearchResults = {
  channels: WorkspaceAgentSmartResizeChannelSize[];
  savedSizes: WorkspaceAgentSmartResizeSavedSize[];
  sets: WorkspaceAgentSmartResizeSizeSet[];
  shapes: WorkspaceAgentSmartResizeShapeOption[];
  typed: WorkspaceAgentSmartResizeSize | null;
};

const EMPTY_WORKSPACE_AGENT_SMART_RESIZE_SEARCH_RESULTS: WorkspaceAgentSmartResizeSearchResults = {
  channels: [],
  savedSizes: [],
  sets: [],
  shapes: [],
  typed: null,
};

export const searchWorkspaceAgentSmartResize = ({
  query,
  savedSets,
  savedSizes,
}: {
  query: string;
  savedSets: readonly WorkspaceAgentSmartResizeSizeSet[];
  savedSizes: readonly WorkspaceAgentSmartResizeSavedSize[];
}): WorkspaceAgentSmartResizeSearchResults => {
  const normalized = query.trim().toLowerCase();
  const typed = normalized ? parseWorkspaceAgentSmartResizeQuery(normalized) : null;

  if (!normalized) {
    return EMPTY_WORKSPACE_AGENT_SMART_RESIZE_SEARCH_RESULTS;
  }

  return {
    channels: WORKSPACE_AGENT_SMART_RESIZE_CHANNELS.filter((channel) =>
      includesQuery(
        normalized,
        channel.label,
        channel.shortLabel,
        channel.ratio,
        `${channel.width} × ${channel.height}`,
        `${channel.width}x${channel.height}`,
      ),
    ),
    savedSizes: savedSizes.filter((saved) =>
      includesQuery(
        normalized,
        saved.name,
        workspaceAgentSmartResizeSizeLabel(saved.size),
        workspaceAgentSmartResizeSizeText(saved.size),
      ),
    ),
    sets: savedSets.filter((sizeSet) => includesQuery(normalized, sizeSet.name)),
    shapes: WORKSPACE_AGENT_SMART_RESIZE_SHAPES.filter((shape) => {
      const label = workspaceAgentSmartResizeSizeLabel(shape.size);
      const ratioName = workspaceAgentSmartResizeRatioLabel(shape.size);

      return includesQuery(normalized, label, ratioName, workspaceAgentSmartResizeSizeText(shape.size));
    }),
    typed,
  };
};

export const selectionMatchesSavedSet = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
  savedSets: readonly WorkspaceAgentSmartResizeSizeSet[],
): boolean =>
  savedSets.some((sizeSet) => sizeSet.sizes.length === sizes.length && sizeSetIsFullySelected(sizes, sizeSet));

export const selectionIsSavedSize = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
  savedSizes: readonly WorkspaceAgentSmartResizeSavedSize[],
): boolean => {
  const [onlySize, ...rest] = sizes;

  if (!onlySize || rest.length > 0) {
    return false;
  }

  return savedSizes.some((saved) => sameWorkspaceAgentSmartResizeDimensions(saved.size, onlySize));
};

export type BuildWorkspaceAgentSmartResizePromptInput = {
  destination: WorkspaceAgentGuidedVariantSaveDestination;
  imageCount: number;
  output?: WorkspaceAgentGuidedVariantOutput;
  sizes: readonly WorkspaceAgentSmartResizeSize[];
  skippedFileCount: number;
};

export const buildWorkspaceAgentSmartResizePrompt = ({
  destination,
  imageCount,
  output = WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  sizes,
  skippedFileCount,
}: BuildWorkspaceAgentSmartResizePromptInput): string => {
  const subject = imageCount === 1 ? 'the selected image or video' : 'each selected image or video';
  const sizeList = sizes.map((size) => workspaceAgentSmartResizeSizeText(size)).join(', ');
  const skipFiles = buildWorkspaceAgentGuidedVariantSkipOtherFilesClause({
    appliesToPhrase: 'smart resize only applies to images and videos',
    skippedFileCount,
  });

  return withWorkspaceAgentGuidedVariantSaveDestinationAndRename(
    `Smart resize ${subject} for each of these sizes: ${sizeList}. Reformat the content and extend the canvas with AI so the subject stays intact.${skipFiles}`,
    destination,
    resolveWorkspaceAgentGuidedVariantPromptRenameSuffix('{size}', output),
    { sizeCount: sizes.length },
  );
};

const groupContainsSelection = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
  group: WorkspaceAgentSmartResizeChannelGroup,
): boolean =>
  group.sizes.some((channel) =>
    isWorkspaceAgentSmartResizeSizeSelected(sizes, workspaceAgentSmartResizeChannelToSize(channel)),
  );

/** Saved and Shape start open. A collapsed channel opens when one of its sizes is already picked. */
export const expandedWorkspaceAgentSmartResizeGroups = (
  sizes: readonly WorkspaceAgentSmartResizeSize[],
): Set<string> => {
  const expanded = new Set<string>(['saved', 'shape']);

  if (groupContainsSelection(sizes, WORKSPACE_AGENT_SMART_RESIZE_ECOMMERCE_GROUP)) {
    expanded.add(WORKSPACE_AGENT_SMART_RESIZE_ECOMMERCE_GROUP.id);
  }

  if (groupContainsSelection(sizes, WORKSPACE_AGENT_SMART_RESIZE_LIFECYCLE_GROUP)) {
    expanded.add(WORKSPACE_AGENT_SMART_RESIZE_LIFECYCLE_GROUP.id);
  }

  if (WORKSPACE_AGENT_SMART_RESIZE_SOCIAL_GROUPS.some((group) => groupContainsSelection(sizes, group))) {
    expanded.add('social');
  }

  return expanded;
};
